/* Lumer Aura Theme - Global JS */

function measureAnnouncementBarHeight() {
  const bar = document.querySelector('[data-announcement-bar]');
  if (bar) return bar.offsetHeight;

  const section = document.querySelector('.announcement-bar-section');
  if (!section) return 0;

  const prev = section.style.maxHeight;
  section.style.maxHeight = 'none';
  const height = section.offsetHeight;
  section.style.maxHeight = prev;
  return height;
}

function syncAnnouncementBarLayout() {
  const menuOpen = document.body.classList.contains('menu-drawer-open');
  const scrollHidden = document.body.classList.contains('announcement-bar-hidden');
  const hidden = menuOpen || scrollHidden;
  const root = document.documentElement;

  if (!hidden) {
    const full = measureAnnouncementBarHeight();
    if (full > 0) {
      root.style.setProperty('--announcement-bar-full-height', `${full}px`);
      root.dataset.announcementBarFullHeight = String(full);
    }
  }

  const stored = parseFloat(root.dataset.announcementBarFullHeight || '')
    || parseFloat(getComputedStyle(root).getPropertyValue('--announcement-bar-full-height'))
    || measureAnnouncementBarHeight();
  root.style.setProperty('--announcement-bar-height', hidden ? '0px' : `${stored}px`);
}

class AnnouncementBar {
  constructor(el) {
    this.el = el;
    el.announcementBarInstance = this;
    this.slides = el.querySelectorAll('.announcement-bar__slide');
    this.track = el.querySelector('.announcement-bar__slides');
    this.current = 0;
    this.speed = parseInt(el.dataset.speed || 3, 10) * 1000;
    this.init();
  }

  init() {
    this.updateHeight();
    window.addEventListener('resize', () => this.updateHeight());
    if (this.slides.length <= 1) return;
    this.el.querySelector('.announcement-bar__arrow--prev')?.addEventListener('click', () => this.prev());
    this.el.querySelector('.announcement-bar__arrow--next')?.addEventListener('click', () => this.next());
    this.timer = setInterval(() => this.next(), this.speed);
    this.el.addEventListener('mouseenter', () => clearInterval(this.timer));
    this.el.addEventListener('mouseleave', () => {
      this.timer = setInterval(() => this.next(), this.speed);
    });
  }

  updateHeight() {
    syncAnnouncementBarLayout();
  }

  goTo(index) {
    this.current = (index + this.slides.length) % this.slides.length;
    const offset = (this.current * 100) / this.slides.length;
    this.track.style.transform = `translateX(-${offset}%)`;
  }

  next() { this.goTo(this.current + 1); }
  prev() { this.goTo(this.current - 1); }
}

class CountdownTimer {
  constructor(el) {
    this.el = el;
    this.target = new Date(el.dataset.timer).getTime();
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  tick() {
    const diff = this.target - Date.now();
    if (diff <= 0) {
      clearInterval(this.interval);
      return;
    }
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    const set = (sel, val) => {
      const el = this.el.querySelector(sel);
      if (el) el.textContent = String(val).padStart(2, '0');
    };
    set('.countdown-day', days);
    set('.countdown-hour', hours);
    set('.countdown-minute', minutes);
    set('.countdown-second', seconds);
  }
}

class SlideshowMedia {
  static init() {
    if (SlideshowMedia.initialized) return;
    SlideshowMedia.initialized = true;

    SlideshowMedia.bindSources(document);

    const syncAll = () => {
      document.querySelectorAll('[data-slideshow]').forEach((root) => SlideshowMedia.syncRoot(root));
    };

    syncAll();
    window.addEventListener('resize', syncAll, { passive: true });
    window.addEventListener('load', syncAll, { once: true });
    window.addEventListener('pageshow', syncAll);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) syncAll();
    });

    const unlock = () => syncAll();
    document.addEventListener('touchstart', unlock, { once: true, passive: true, capture: true });
    document.addEventListener('click', unlock, { once: true, passive: true, capture: true });

    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const root = entry.target.matches('[data-slideshow]')
            ? entry.target
            : entry.target.closest('[data-slideshow]');
          if (root) SlideshowMedia.syncRoot(root);
        });
      }, { threshold: 0.12 });

      document.querySelectorAll('[data-slideshow]').forEach((root) => observer.observe(root));
    }

    setTimeout(syncAll, 250);
    setTimeout(syncAll, 900);
  }

  static isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  static resolveVideoUrl(video) {
    if (!video?.dataset) return '';
    return SlideshowMedia.isMobileViewport()
      ? (video.dataset.mobileSrc || video.dataset.desktopSrc || '')
      : (video.dataset.desktopSrc || video.dataset.mobileSrc || '');
  }

  static bindSources(root) {
    root.querySelectorAll('[data-slideshow-video]').forEach((video) => {
      SlideshowMedia.ensureVideoSource(video, video.closest('.slideshow__slide')?.classList.contains('is-active'));
    });
  }

  static ensureVideoSource(video, active) {
    const url = SlideshowMedia.resolveVideoUrl(video);
    if (!url) return false;

    if (video.dataset.resolvedSrc !== url) {
      video.dataset.resolvedSrc = url;
      video.src = url;
      video.load();
    } else if (active && !video.getAttribute('src')) {
      video.src = url;
      video.load();
    }

    return active;
  }

  static shouldPlay(video) {
    if (video.classList.contains('slideshow__video--responsive')) return true;
    const mobile = SlideshowMedia.isMobileViewport();
    const variant = video.dataset.slideshowVariant
      || (video.classList.contains('slideshow__video--mobile') ? 'mobile' : 'desktop');
    return mobile ? variant === 'mobile' : variant === 'desktop';
  }

  static playVideo(video) {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const markReady = () => {
      video.closest('.slideshow__media')?.classList.add('is-video-ready');
    };

    if (video.readyState >= 2) markReady();
    else {
      video.addEventListener('loadeddata', markReady, { once: true });
      video.addEventListener('playing', markReady, { once: true });
    }

    const attempt = () => {
      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          setTimeout(() => video.play().catch(() => {}), 120);
        });
      }
    };

    if (video.readyState >= 2) {
      attempt();
      return;
    }

    video.addEventListener('loadeddata', attempt, { once: true });
    video.addEventListener('canplay', attempt, { once: true });
    attempt();
  }

  static syncRoot(root) {
    root.querySelectorAll('.slideshow__slide').forEach((slide) => {
      const active = slide.classList.contains('is-active');
      slide.querySelectorAll('.slideshow__video').forEach((video) => {
        if (!active) {
          video.pause();
          return;
        }

        SlideshowMedia.ensureVideoSource(video, true);

        if (!SlideshowMedia.shouldPlay(video)) {
          video.pause();
          return;
        }

        SlideshowMedia.playVideo(video);
      });
    });
  }
}

class Slideshow {
  constructor(el) {
    this.el = el;
    this.slides = el.querySelectorAll('.slideshow__slide');
    this.current = 0;
    this.speed = parseInt(el.dataset.speed || 8, 10) * 1000;
    SlideshowMedia.syncRoot(el);
    if (this.slides.length <= 1) return;
    this.init();
  }

  init() {
    this.dots = this.el.querySelectorAll('.slideshow__dot');
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
    this.timer = setInterval(() => this.next(), this.speed);
  }

  goTo(index) {
    this.slides[this.current].classList.remove('is-active');
    this.dots[this.current]?.classList.remove('is-active');
    this.current = index;
    this.slides[this.current].classList.add('is-active');
    this.dots[this.current]?.classList.add('is-active');
    SlideshowMedia.syncRoot(this.el);
  }

  next() { this.goTo((this.current + 1) % this.slides.length); }
}

class ProductCarousel {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('[data-carousel-track]') || el.querySelector('.product-grid--scroll');
    if (!this.track) return;
    this.isLookbookSingle = el.hasAttribute('data-lookbook-single-carousel');
    this.prev = el.querySelector('[data-carousel-prev]');
    this.next = el.querySelector('[data-carousel-next]');
    this.status = el.querySelector('[data-carousel-status]');
    this.slides = () => [...this.track.querySelectorAll('[data-carousel-slide], .product-card-wrap, .splide__slide')];

    const scrollAmount = () => {
      if (this.isLookbookSingle && window.matchMedia('(min-width: 990px)').matches) {
        return this.track.clientWidth;
      }
      const card = this.track.querySelector('.product-card, .product-card-wrap, .splide__slide, .collection-card');
      const gap = parseFloat(getComputedStyle(this.track).gap) || 16;
      return (card?.offsetWidth || 280) + gap;
    };

    const scrollStep = (direction) => {
      if (this.isLookbookSingle && window.matchMedia('(min-width: 990px)').matches) {
        const slideWidth = this.track.clientWidth || 1;
        const maxIndex = Math.max(this.slides().length - 1, 0);
        const currentIndex = Math.round(this.track.scrollLeft / slideWidth);
        const nextIndex = Math.min(Math.max(currentIndex + direction, 0), maxIndex);
        this.track.scrollTo({ left: nextIndex * slideWidth, behavior: 'smooth' });
        return;
      }
      this.track.scrollBy({ left: direction * scrollAmount(), behavior: 'smooth' });
    };

    this.prev?.addEventListener('click', () => scrollStep(-1));
    this.next?.addEventListener('click', () => scrollStep(1));

    this.dots = el.querySelectorAll('[data-carousel-dot]');
    if (this.dots.length && this.track) {
      const scrollToIndex = (index) => {
        const slides = this.slides();
        const card = slides[0];
        if (!card) return;
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        this.track.scrollTo({ left: index * (card.offsetWidth + gap), behavior: 'smooth' });
      };

      const updateDots = () => {
        const slides = this.slides();
        if (!slides.length) return;
        const card = slides[0];
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        const step = card.offsetWidth + gap;
        const index = step > 0 ? Math.min(Math.round(this.track.scrollLeft / step), this.dots.length - 1) : 0;
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      };

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => scrollToIndex(i));
      });

      this.track.addEventListener('scroll', updateDots, { passive: true });
      window.addEventListener('resize', updateDots, { passive: true });
      updateDots();
    }

    if (this.isLookbookSingle && this.status) {
      const updateStatus = () => {
        const slideWidth = this.track.clientWidth || 1;
        const index = Math.round(this.track.scrollLeft / slideWidth);
        const total = this.slides().length;
        this.status.textContent = `${Math.min(index + 1, total)} / ${total}`;
      };
      this.track.addEventListener('scroll', updateStatus, { passive: true });
      window.addEventListener('resize', updateStatus, { passive: true });
      updateStatus();
    }
  }
}

class QuickView {
  constructor() {
    this.modal = document.getElementById('quick-view-modal');
    if (!this.modal) return;
    this.content = this.modal.querySelector('.quick-view-modal__content');
    this.overlay = this.modal.querySelector('.quick-view-modal__overlay');
    this.closeBtn = this.modal.querySelector('.quick-view-modal__close');
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.quick-view-btn');
      if (!btn) return;
      e.preventDefault();
      this.open(btn.dataset.quickViewUrl);
    });
    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
  }

  async open(url) {
    if (!url || !this.content) return;
    this.content.innerHTML = '<p style="padding:2rem;text-align:center">Loading…</p>';
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    try {
      const res = await fetch(`${url}?view=quick`);
      if (res.ok) {
        this.content.innerHTML = await res.text();
      } else {
        const res2 = await fetch(url);
        const html = await res2.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const main = doc.querySelector('.product-page, .product-page__grid, main');
        this.content.innerHTML = main ? main.innerHTML : `<p style="padding:2rem"><a href="${url}" class="btn btn-primary">View product</a></p>`;
      }
    } catch {
      this.content.innerHTML = `<p style="padding:2rem"><a href="${url}" class="btn btn-primary">View product</a></p>`;
    }
  }

  close() {
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;
    this.sectionId = 'cart-drawer';

    document.querySelectorAll('[data-cart-open]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
    });

    this.bindDrawerEvents();
  }

  bindDrawerEvents() {
    if (!this.drawer) return;

    this.drawer.querySelectorAll('[data-cart-close]').forEach(el => {
      el.onclick = () => this.close();
    });

    this.drawer.onclick = (e) => {
      const minus = e.target.closest('[data-quantity-minus]');
      const plus = e.target.closest('[data-quantity-plus]');
      const remove = e.target.closest('[data-cart-remove]');
      const apply = e.target.closest('[data-discount-apply]');
      const checkout = e.target.closest('[data-cart-checkout]');

      if (minus || plus) {
        const input = (minus || plus).closest('quantity-input')?.querySelector('[data-quantity-input]');
        if (!input) return;
        const line = input.dataset.index;
        let qty = parseInt(input.value, 10) || 0;
        qty = minus ? Math.max(0, qty - 1) : qty + 1;
        this.changeLine(line, qty);
      }

      if (remove) {
        this.changeLine(remove.dataset.line, 0);
      }

      if (apply) {
        e.preventDefault();
        const code = this.drawer.querySelector('[data-discount-input]')?.value?.trim();
        if (code) this.applyDiscount(code);
      }

      if (checkout) {
        e.preventDefault();
        this.goToCheckout();
      }
    };

    this.drawer.onchange = (e) => {
      const input = e.target.closest('[data-quantity-input]');
      if (!input) return;
      this.changeLine(input.dataset.index, parseInt(input.value, 10) || 0);
    };
  }

  open() {
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.refresh();
  }

  close() {
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async refresh() {
    try {
      const res = await fetch(`${window.routes?.cart_url || '/cart'}?section_id=${this.sectionId}`);
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('[data-cart-drawer]');
      if (!incoming) return;
      this.drawer.innerHTML = incoming.innerHTML;
      this.drawer.classList.toggle('is-empty', incoming.classList.contains('is-empty'));
      this.bindDrawerEvents();
      await this.updateCount();
    } catch {
      /* keep current drawer state */
    }
  }

  async changeLine(line, quantity) {
    try {
      const res = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!res.ok) throw new Error('Cart update failed');
      await this.refresh();
      if (quantity === 0) this.announce('Item removed');
    } catch {
      this.announce('Unable to update cart');
    }
  }

  async applyDiscount(code) {
    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, { method: 'POST' });
      await this.refresh();
    } catch {
      const err = this.drawer.querySelector('[data-discount-error]');
      if (err) {
        err.textContent = 'Discount code could not be applied';
        err.classList.remove('hidden');
      }
    }
  }

  goToCheckout() {
    const checkoutBtn = this.drawer.querySelector('[data-cart-checkout]');
    if (checkoutBtn?.disabled) return;

    const form = document.getElementById('CartDrawer-Form');
    if (form) {
      if (!form.querySelector('input[name="checkout"]')) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'checkout';
        form.appendChild(input);
      }
      form.submit();
      return;
    }

    window.location.assign(window.routes?.checkout_url || '/checkout');
  }

  announce(message) {
    const region = this.drawer.querySelector('#CartDrawer-LiveRegionText');
    if (region) region.textContent = message;
  }

  async updateCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('.cart-count-bubble').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
    } catch { /* noop */ }
  }

  async add(form) {
    const formData = new FormData(form);
    const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' }
    });
    if (res.ok) {
      await this.updateCount();
      this.open();
    }
    return res;
  }
}

class MenuDrawer {
  constructor() {
    this.bind();
  }

  bind() {
    this.drawer = document.getElementById('menu-drawer');
    if (!this.drawer || this.drawer.dataset.menuBound === 'true') return;
    this.drawer.dataset.menuBound = 'true';

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-menu-open]')) {
        e.preventDefault();
        this.open();
      }
    });

    this.drawer.querySelector('.menu-drawer__overlay')?.addEventListener('click', () => this.close());
    this.drawer.querySelector('.menu-drawer__close')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.close();
    });
  }

  open() {
    this.drawer = document.getElementById('menu-drawer');
    if (!this.drawer) return;
    document.body.classList.add('menu-drawer-open');
    document.documentElement.style.setProperty('--announcement-bar-height', '0px');
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.drawer?.classList.remove('is-open');
    document.body.classList.remove('menu-drawer-open');
    document.body.style.overflow = '';
    window.headerMetrics?.update();
  }
}

class HeaderMetrics {
  constructor() {
    this.announcement = document.querySelector('[data-announcement-bar]');
    this.header = document.querySelector('[data-header-root], .header-wrapper');
    this.update = this.update.bind(this);
    this.update();
    window.addEventListener('resize', this.update, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(this.update);
      const annSection = document.querySelector('.announcement-bar-section');
      if (annSection) ro.observe(annSection);
      if (this.announcement) ro.observe(this.announcement);
      if (this.header) ro.observe(this.header);
    }
  }

  update() {
    syncAnnouncementBarLayout();
    const hdr = this.header?.offsetHeight || 64;
    document.documentElement.style.setProperty('--header-height', `${hdr}px`);
  }
}

class AnnouncementBarScroll {
  constructor() {
    if (!document.querySelector('.announcement-bar-section')) return;

    this.scrollRoot = document.scrollingElement || document.documentElement;
    this.lastY = this.getScrollY();
    this.hidden = document.body.classList.contains('announcement-bar-hidden');
    this.accumDown = 0;
    this.ticking = false;
    this.hideStart = 44;
    this.hideDistance = 28;
    this.showTop = 5;
    this.showUpZone = 128;

    const onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.sync();
        this.ticking = false;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true });
  }

  getScrollY() {
    return window.scrollY || this.scrollRoot.scrollTop || document.body.scrollTop || 0;
  }

  sync() {
    if (document.body.classList.contains('menu-drawer-open')) return;

    const y = this.getScrollY();
    const delta = y - this.lastY;

    if (y <= this.showTop) {
      this.accumDown = 0;
      this.setHidden(false);
    } else if (delta > 0.5) {
      this.accumDown += delta;
      if (y > this.hideStart && this.accumDown > this.hideDistance) {
        this.setHidden(true);
      }
    } else if (delta < -0.5) {
      this.accumDown = 0;
      if (y < this.showUpZone) {
        this.setHidden(false);
      }
    }

    this.lastY = y;
  }

  setHidden(hidden) {
    if (this.hidden === hidden) return;
    this.hidden = hidden;
    document.body.classList.toggle('announcement-bar-hidden', hidden);
    window.headerMetrics?.update();
  }
}

class StickyHeader {
  constructor() {
    this.header = document.querySelector('[data-header-root], .header-wrapper');
    if (!this.header?.classList.contains('header-wrapper--transparent')) return;

    const onScroll = () => {
      this.header.classList.toggle('scrolled', window.scrollY > 50);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }
}

class TransparentHeader {
  constructor() {
    if (document.body.classList.contains('template-index')) return;
    this.header = document.querySelector('[data-header-root].header-wrapper--transparent, .header-wrapper--transparent');
    this.hero = document.querySelector('.slideshow, .section-slideshow');
    if (!this.header || !this.hero) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        this.header.classList.toggle('header--on-light', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px 0px -20% 0px' }
    );
    observer.observe(this.hero);
  }
}

class ProductForm {
  constructor() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-product-form]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      btn?.setAttribute('disabled', 'true');
      const res = await window.cartDrawer?.add(form);
      btn?.removeAttribute('disabled');
      if (res?.ok) {
        const variantId = parseInt(form.querySelector('[data-variant-id]')?.value, 10);
        if (variantId) {
          fetch(`${window.routes?.cart_url || '/cart'}.js`)
            .then((r) => r.json())
            .then((cart) => {
              const qty = cart.items.find((item) => item.variant_id === variantId)?.quantity || 0;
              document.querySelectorAll('[data-cart-qty-note]').forEach((el) => {
                el.textContent = qty > 0 ? `(${qty} in cart)` : '';
              });
            })
            .catch(() => {});
        }
      }
    });
  }
}

class MediaGallery {
  constructor() {
    document.querySelectorAll('[data-media-gallery]').forEach(gallery => {
      const carousel = gallery.querySelector('.media-gallery__carousel');
      const slides = carousel ? [...carousel.querySelectorAll('.media-gallery__slide')] : [];
      const thumbs = carousel ? [...carousel.querySelectorAll('[data-media-thumb]')] : [];
      const counter = carousel?.querySelector('[data-media-current]');
      const maxIndex = Math.max(0, slides.length - 1);

      const goTo = (index) => {
        const i = Math.max(0, Math.min(index, maxIndex));
        slides.forEach((s) => s.classList.toggle('is-active', parseInt(s.dataset.mediaIndex, 10) === i));
        thumbs.forEach((t) => t.classList.toggle('is-active', parseInt(t.dataset.mediaIndex, 10) === i));
        if (counter) counter.textContent = String(i + 1);
        gallery.dataset.currentMediaIndex = String(i);
      };

      gallery.goToMediaIndex = goTo;

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => goTo(parseInt(thumb.dataset.mediaIndex, 10)));
      });

      carousel?.querySelector('[data-media-prev]')?.addEventListener('click', () => {
        const current = parseInt(gallery.dataset.currentMediaIndex || '0', 10);
        goTo(current - 1);
      });
      carousel?.querySelector('[data-media-next]')?.addEventListener('click', () => {
        const current = parseInt(gallery.dataset.currentMediaIndex || '0', 10);
        goTo(current + 1);
      });

      if (slides.length) goTo(0);
    });
  }
}

class ProductVariantPicker {
  constructor() {
    document.querySelectorAll('variant-selects').forEach(picker => {
      const section = picker.dataset.section;
      const productJson = document.getElementById(`ProductJSON-${section}`);
      if (!productJson) return;

      const product = JSON.parse(productJson.textContent);
      const form = document.querySelector(`#product-form-${section}`);
      const idInput = form?.querySelector('[data-variant-id]');

      picker.querySelectorAll('.swatch-input').forEach(input => {
        input.addEventListener('change', () => {
          picker.querySelectorAll(`input[name="${CSS.escape(input.name)}"]`).forEach(radio => {
            const label = picker.querySelector(`label[for="${radio.id}"]`);
            label?.classList.toggle('is-active', radio.checked);
          });

          const fieldset = input.closest('fieldset[data-option-index]');
          const labelValue = fieldset?.querySelector('.form__label-value');
          if (labelValue) labelValue.textContent = input.value;

          const selectedOptions = [];
          picker.querySelectorAll('fieldset[data-option-index]').forEach(fieldset => {
            const checked = fieldset.querySelector('.swatch-input:checked');
            if (checked) selectedOptions.push(checked.value);
          });

          const variant = product.variants.find(v =>
            v.options.every((opt, i) => opt === selectedOptions[i])
          );

          if (variant && idInput) {
            idInput.value = variant.id;
            this.updateProductInfo(section, variant, product);
          }
        });
      });

      const initialVariant = product.variants.find((v) => v.id === parseInt(idInput?.value, 10)) || product.variants[0];
      if (initialVariant) this.updateCartQuantityNote(initialVariant.id);
    });
  }

  getMediaIndexForVariant(variant) {
    const mediaId = variant.featured_media?.id || variant.featured_image?.id;
    if (!mediaId) return -1;
    const orderEl = document.querySelector('[data-product-media-order]');
    if (!orderEl) return -1;
    try {
      const order = JSON.parse(orderEl.textContent);
      const entry = order.find((m) => m.id === mediaId);
      return entry?.index ?? -1;
    } catch {
      return -1;
    }
  }

  updateMediaForVariant(variant) {
    const mediaId = variant.featured_media?.id || variant.featured_image?.id;
    const gallery = document.querySelector('[data-media-gallery]');
    if (!gallery) return;

    const index = this.getMediaIndexForVariant(variant);
    if (index >= 0 && typeof gallery.goToMediaIndex === 'function') {
      gallery.goToMediaIndex(index);
      gallery.querySelectorAll('.media-gallery__grid-item').forEach((el) => {
        el.classList.toggle('is-featured', parseInt(el.dataset.mediaIndex, 10) === index);
      });
      const gridItem = gallery.querySelector(`[data-media-id="${mediaId}"]`);
      gridItem?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    const stickyImg = document.querySelector('.product-sticky-bar__image');
    const imageSrc = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
    if (stickyImg && imageSrc) stickyImg.src = imageSrc;
  }

  updateStickyBar(sectionId, variant) {
    const bar = document.querySelector(`[data-product-sticky-bar][data-section-id="${sectionId}"]`);
    if (!bar) return;

    const priceEl = bar.querySelector('[data-sticky-price]');
    const optionsEl = bar.querySelector('[data-sticky-options]');
    const atcBtn = bar.querySelector('[data-sticky-atc]');

    if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
    if (optionsEl && variant.options) optionsEl.textContent = variant.options.join(' · ');
    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }
  }

  updateCartQuantityNote(variantId) {
    fetch(`${window.routes?.cart_url || '/cart'}.js`)
      .then((res) => res.json())
      .then((cart) => {
        const qty = cart.items.find((item) => item.variant_id === variantId)?.quantity || 0;
        document.querySelectorAll('[data-cart-qty-note]').forEach((el) => {
          el.textContent = qty > 0 ? `(${qty} in cart)` : '';
        });
      })
      .catch(() => {});
  }

  formatMoney(cents) {
    const format = window.theme?.moneyFormat || '${{amount}}';
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, format);
    }
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  updateProductInfo(sectionId, variant, product) {
    const priceEl = document.querySelector(`#price-${sectionId}`);
    if (priceEl) {
      const regular = priceEl.querySelector('[data-price-regular]');
      const saleWrap = priceEl.querySelector('.price__sale');
      const sale = priceEl.querySelector('[data-price-sale]');
      const compare = priceEl.querySelector('[data-price-compare]');
      const saleBadge = priceEl.querySelector('.price__badge-sale');
      const money = this.formatMoney(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (regular) regular.textContent = money;
      if (sale) sale.textContent = money;
      if (compare && variant.compare_at_price) compare.textContent = this.formatMoney(variant.compare_at_price);
      saleWrap?.classList.toggle('hidden', !onSale);
      if (saleBadge) {
        saleBadge.style.display = onSale ? 'inline-block' : 'none';
        if (onSale) {
          const saved = variant.compare_at_price - variant.price;
          saleBadge.textContent = `Save ${this.formatMoney(saved)}`;
        }
      }
      const soldBadge = priceEl.querySelector('.price__badge-sold-out');
      if (soldBadge) soldBadge.style.display = variant.available ? 'none' : 'inline-block';
    }

    const submitBtn = document.querySelector(`#ProductSubmitButton-${sectionId}`);
    const submitText = submitBtn?.querySelector('[data-add-to-cart-text]');
    const backInStock = document.querySelector('[data-back-in-stock]');

    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      if (submitText) {
        submitText.textContent = variant.available
          ? (submitBtn.dataset.labelAdd || 'Add to cart')
          : (submitBtn.dataset.labelSoldOut || 'Sold out');
      }
    }
    backInStock?.classList.toggle('hidden', variant.available);

    const jsonScript = document.querySelector(`variant-selects[data-section="${sectionId}"] [data-selected-variant]`);
    if (jsonScript) jsonScript.textContent = JSON.stringify(variant);

    document.querySelectorAll(`#product-form-installment-${sectionId} [name="id"]`).forEach(input => {
      input.value = variant.id;
    });

    this.updateMediaForVariant(variant);
    this.updateStickyBar(sectionId, variant);
    this.updateCartQuantityNote(variant.id);
  }
}

class ProductLightbox {
  constructor() {
    this.root = document.querySelector('[data-product-lightbox]');
    if (!this.root) return;
    this.img = this.root.querySelector('[data-lightbox-image]');
    this.counter = this.root.querySelector('[data-lightbox-counter]');
    this.sources = [];
    this.index = 0;

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-media-zoom]');
      if (!btn) return;
      e.preventDefault();
      const gallery = btn.closest('[data-media-gallery]');
      if (!gallery) return;
      this.sources = [...gallery.querySelectorAll('[data-media-zoom]')]
        .map((b) => b.dataset.mediaZoom)
        .filter(Boolean);
      this.index = this.sources.indexOf(btn.dataset.mediaZoom);
      if (this.index < 0) this.index = 0;
      this.open();
    });

    this.root.querySelectorAll('[data-lightbox-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });
    this.root.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.step(-1));
    this.root.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.step(1));
    document.addEventListener('keydown', (e) => {
      if (this.root.hidden) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowLeft') this.step(-1);
      if (e.key === 'ArrowRight') this.step(1);
    });
  }

  open() {
    this.render();
    this.root.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.root.hidden = true;
    document.body.style.overflow = '';
  }

  render() {
    if (!this.sources.length || !this.img) return;
    this.img.src = this.sources[this.index];
    if (this.counter) {
      const multi = this.sources.length > 1;
      this.counter.hidden = !multi;
      if (multi) this.counter.textContent = `${this.index + 1} / ${this.sources.length}`;
    }
  }

  step(dir) {
    if (this.sources.length < 2) return;
    this.index = (this.index + dir + this.sources.length) % this.sources.length;
    this.render();
  }
}

class ProductStickyBar {
  constructor() {
    document.querySelectorAll('[data-product-sticky-bar]').forEach((bar) => {
      const sectionId = bar.dataset.sectionId;
      const submitBtn = document.querySelector(`#ProductSubmitButton-${sectionId}`);
      const mainForm = document.querySelector(`#product-form-${sectionId}`);
      if (!submitBtn || !mainForm) return;

      const show = () => bar.classList.add('is-visible');
      const hide = () => bar.classList.remove('is-visible');

      if (typeof IntersectionObserver !== 'undefined') {
        const observer = new IntersectionObserver(
          ([entry]) => (entry.isIntersecting ? hide() : show()),
          { threshold: 0, rootMargin: '0px' }
        );
        observer.observe(submitBtn);
      }

      bar.querySelector('[data-sticky-atc]')?.addEventListener('click', () => {
        if (submitBtn.disabled) return;
        submitBtn.click();
      });
    });
  }
}

class QuantityInput {
  constructor() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.quantity__button');
      if (!btn || btn.closest('[data-cart-drawer]')) return;
      const wrap = btn.closest('quantity-input');
      const input = wrap?.querySelector('[data-quantity-input]');
      if (!input) return;
      const min = parseInt(input.min, 10) || 1;
      const max = input.max ? parseInt(input.max, 10) : Infinity;
      let val = parseInt(input.value, 10) || min;
      if (btn.name === 'minus') val = Math.max(min, val - 1);
      if (btn.name === 'plus') val = Math.min(max, val + 1);
      input.value = val;
    });
  }
}

class ProductTabs {
  constructor() {
    document.querySelectorAll('[data-product-tabs]').forEach(section => {
      const buttons = section.querySelectorAll('.tab-button');
      const panels = section.querySelectorAll('.tab-content');
      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          const target = btn.dataset.tab;
          buttons.forEach(b => {
            b.classList.toggle('active', b === btn);
            b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
          });
          panels.forEach(panel => {
            const active = panel.id === target;
            panel.classList.toggle('active', active);
            panel.hidden = !active;
          });
        });
      });
    });
  }
}

class RelatedProducts {
  constructor() {
    document.querySelectorAll('product-recommendations.related-products').forEach(el => {
      if (el.querySelector('.related-products--inner')) {
        if (el.closest('[data-product-carousel]')) {
          const track = el.querySelector('[data-carousel-track]');
          if (track) new ProductCarousel(el.closest('[data-product-carousel]'));
        }
        return;
      }
      const url = el.dataset.url;
      if (!url) return;
      fetch(url)
        .then(res => res.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section = doc.querySelector('.section-related-products');
          if (section) {
            el.innerHTML = section.querySelector('product-recommendations')?.innerHTML || section.innerHTML;
            const carousel = el.closest('[data-product-carousel]');
            if (carousel) new ProductCarousel(carousel);
          }
        })
        .catch(() => {});
    });
  }
}

class ComplementaryProducts {
  constructor() {
    document.querySelectorAll('[data-complementary]').forEach(el => {
      const url = el.dataset.url;
      if (!url) return;
      fetch(url)
        .then(res => res.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('.complementary-products--inner');
          if (inner) {
            el.innerHTML = inner.outerHTML;
            this.initCarousel(el);
          }
        })
        .catch(() => {});
    });
  }

  initCarousel(container) {
    const track = container.querySelector('[data-complementary-track]');
    if (!track) return;
    const scrollAmount = () => {
      const slide = track.querySelector('.complementary-products__slide');
      return (slide?.offsetWidth || 200) + 48;
    };
    container.querySelector('[data-complementary-prev]')?.addEventListener('click', () => {
      track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });
    container.querySelector('[data-complementary-next]')?.addEventListener('click', () => {
      track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });
  }
}

class NotifyModal {
  constructor() {
    document.querySelectorAll('[data-notify-open]').forEach(btn => {
      btn.addEventListener('click', () => {
        const modal = btn.closest('[data-product-section]')?.querySelector('[data-notify-modal]');
        modal?.removeAttribute('hidden');
      });
    });
    document.querySelectorAll('[data-notify-close]').forEach(btn => {
      btn.addEventListener('click', () => btn.closest('[data-notify-modal]')?.setAttribute('hidden', ''));
    });
    document.querySelectorAll('[data-notify-form]').forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        form.closest('[data-notify-modal]')?.setAttribute('hidden', '');
      });
    });
  }
}

class ScrollReveal {
  constructor() {
    const els = document.querySelectorAll('.animate-on-scroll');
    if (!els.length) return;

    const reveal = (el) => el.classList.add('is-visible');

    const showIfInView = (el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0) reveal(el);
    };

    els.forEach(showIfInView);

    if (typeof IntersectionObserver === 'undefined') {
      els.forEach(reveal);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) reveal(entry.target);
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -5% 0px' });

    els.forEach(el => {
      observer.observe(el);
      showIfInView(el);
    });

    window.addEventListener('load', () => els.forEach(showIfInView), { once: true });
  }
}

class LookbookHotspots {
  constructor() {
    document.querySelectorAll('[data-lookbook]').forEach((section) => {
      const hotspots = section.querySelectorAll('.hotspot');
      if (!hotspots.length) return;
      const track = section.querySelector('[data-carousel-track]');
      const slides = section.querySelectorAll('[data-lookbook-slide]');

      const activate = (index) => {
        const indexStr = String(index);
        hotspots.forEach((h) => {
          const active = h.dataset.lookbookIndex === indexStr;
          h.classList.toggle('is-active', active);
          h.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        slides.forEach((s) => {
          s.classList.toggle('is-lookbook-active', s.dataset.lookbookIndex === indexStr);
        });

        const slide = slides[index];
        if (!slide || !track) return;

        const trackStyle = window.getComputedStyle(track);
        if (trackStyle.display === 'grid') return;

        const offset = slide.offsetLeft - track.offsetLeft;
        track.scrollTo({ left: offset, behavior: 'smooth' });
      };

      hotspots.forEach((hs) => {
        hs.addEventListener('click', () => activate(hs.dataset.lookbookIndex));
      });

      slides.forEach((slide) => {
        slide.addEventListener('click', (event) => {
          if (event.target.closest('a, button, input, form')) return;
          activate(slide.dataset.lookbookIndex);
        });
      });

      if (track && slides.length && 'IntersectionObserver' in window) {
        const trackStyle = window.getComputedStyle(track);
        if (trackStyle.display !== 'grid') {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
                  activate(entry.target.dataset.lookbookIndex);
                }
              });
            },
            { root: track, threshold: [0.55, 0.75] }
          );
          slides.forEach((slide) => observer.observe(slide));
        }
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const safe = (fn) => {
    try {
      fn();
    } catch (err) {
      console.error('[theme]', err);
    }
  };

  document.querySelectorAll('[data-announcement-bar]').forEach(el => safe(() => new AnnouncementBar(el)));
  document.querySelectorAll('[data-countdown]').forEach(el => safe(() => new CountdownTimer(el)));
  safe(() => SlideshowMedia.init());
  document.querySelectorAll('[data-slideshow]').forEach(el => safe(() => new Slideshow(el)));
  document.querySelectorAll('[data-product-carousel]').forEach(el => safe(() => new ProductCarousel(el)));

  safe(() => { window.cartDrawer = new CartDrawer(); });
  safe(() => new QuickView());
  safe(() => new MenuDrawer());
  safe(() => { window.headerMetrics = new HeaderMetrics(); });
  safe(() => new AnnouncementBarScroll());
  safe(() => new StickyHeader());
  safe(() => new TransparentHeader());
  safe(() => new ProductForm());
  safe(() => new MediaGallery());
  safe(() => new ProductLightbox());
  safe(() => new ProductVariantPicker());
  safe(() => new ProductStickyBar());
  safe(() => new QuantityInput());
  safe(() => new ProductTabs());
  safe(() => new ComplementaryProducts());
  safe(() => new RelatedProducts());
  safe(() => new NotifyModal());
  safe(() => new ScrollReveal());
  safe(() => new LookbookHotspots());

  window.cartDrawer?.updateCount();
});
